import React, { useRef } from 'react';
import { Alert, Input, Space, Tag, Typography } from 'antd';
import type { InputRef } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import type { PushLanguage } from '@/types/matchPushes';
import {
  COPY_VARIABLES,
  PUSH_FONT,
  SAMPLE_MATCH,
  copyProblems,
  copyWarnings,
  renderTemplate,
} from '@/utils/matchPushCopy';
import { PhoneNotification } from './PhoneNotification';

const { Text } = Typography;

export interface CopyValue {
  title: string;
  body: string;
}

interface Props {
  language: PushLanguage;
  value: CopyValue;
  /** An empty pair is allowed: the language then uses the Copy tab text. */
  optional?: boolean;
  onChange: (value: CopyValue) => void;
}

type Field = 'title' | 'body';

/**
 * Title and body inputs with clickable variable chips and a live preview of
 * a sample match (New Zealand vs India). The preview mirrors the server's
 * renderer; the server validates on save.
 */
export const CopyEditor: React.FC<Props> = ({ language, value, optional, onChange }) => {
  const titleRef = useRef<InputRef>(null);
  const bodyRef = useRef<TextAreaRef>(null);
  const lastField = useRef<Field>('body');
  const caret = useRef<Record<Field, [number, number]>>({ title: [0, 0], body: [0, 0] });

  const remember =
    (field: Field) => (event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      lastField.current = field;
      const el = event.currentTarget;
      caret.current[field] = [
        el.selectionStart ?? el.value.length,
        el.selectionEnd ?? el.value.length,
      ];
    };

  const insert = (name: string) => {
    const field = lastField.current;
    const token = `{${name}}`;
    const text = value[field];
    const [start, end] = caret.current[field];
    const next = text.slice(0, start) + token + text.slice(end);
    onChange({ ...value, [field]: next });
    const position = start + token.length;
    caret.current[field] = [position, position];
    setTimeout(() => {
      const el =
        field === 'title' ? titleRef.current?.input : bodyRef.current?.resizableTextArea?.textArea;
      if (el) {
        el.focus();
        el.setSelectionRange(position, position);
      }
    }, 0);
  };

  const vars = SAMPLE_MATCH[language];
  const untouched = optional && !value.title.trim() && !value.body.trim();
  const problems = untouched ? [] : copyProblems(value.title, value.body, language);
  const warnings = copyWarnings(value.title, value.body);

  return (
    <div>
      <Space size={[6, 6]} wrap style={{ marginBottom: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Insert:
        </Text>
        {COPY_VARIABLES.map((v) => (
          <Tag
            key={v.name}
            color="blue"
            style={{ cursor: 'pointer', userSelect: 'none', marginInlineEnd: 0 }}
            title={v.help}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => insert(v.name)}
          >
            {`{${v.name}}`}
          </Tag>
        ))}
      </Space>
      {untouched && (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
          Leave both empty to use the Copy tab text in this language.
        </Text>
      )}
      <Input
        ref={titleRef}
        value={value.title}
        placeholder="Title"
        showCount
        style={{ fontFamily: PUSH_FONT, marginBottom: 8 }}
        onChange={(e) => onChange({ ...value, title: e.target.value })}
        onSelect={remember('title')}
        onFocus={remember('title')}
        onKeyUp={remember('title')}
        onClick={remember('title')}
      />
      <Input.TextArea
        ref={bodyRef}
        value={value.body}
        placeholder="Body"
        rows={3}
        showCount
        style={{ fontFamily: PUSH_FONT }}
        onChange={(e) => onChange({ ...value, body: e.target.value })}
        onSelect={remember('body')}
        onFocus={remember('body')}
        onKeyUp={remember('body')}
        onClick={remember('body')}
      />
      {problems.length > 0 && (
        <Alert type="error" showIcon style={{ marginTop: 8 }} message={problems.join('. ')} />
      )}
      {warnings.length > 0 && (
        <Alert type="warning" showIcon style={{ marginTop: 8 }} message={warnings.join('. ')} />
      )}
      <div style={{ marginTop: 12, maxWidth: 360 }}>
        <PhoneNotification
          caption="Preview with a sample match"
          title={renderTemplate(value.title, vars)}
          body={renderTemplate(value.body, vars)}
          note={`${vars.us} vs ${vars.them}, ${vars.match_no}, ${vars.city}, ${vars.time} IST`}
        />
      </div>
    </div>
  );
};
